import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import EnhancedThemeToggle from '../common/EnhancedThemeToggle';
import { ChevronDown, LogOut, Menu, X, User, Calendar, Settings, Home, Package, ShoppingCart } from 'lucide-react';
import logo from '../../assets/bl_logo-bg.png';

const ImprovedNavbar = () => {
  const { currentUser, userRole, userProfile, logout } = useAuth();
  const { getTotalItems, openCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Check if on inventory page and can add to cart
  const isOnInventoryPage = location.pathname.includes('/inventory');
  const canAddToCart = userRole === 'project_manager';
  const cartItemCount = getTotalItems();
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const getDisplayName = () => {
    if (userProfile?.firstName && userProfile?.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName}`;
    }
    
    return currentUser?.email;
  };

  const getInitials = () => {
    if (userProfile?.firstName && userProfile?.lastName) {
      return `${userProfile.firstName[0]}${userProfile.lastName[0]}`.toUpperCase();
    }
    
    return currentUser?.email?.[0].toUpperCase() || '?';
  };

  // Role-specific navigation items
// In ImprovedNavbar.tsx
const getNavigationItems = () => {
  // Common items regardless of role
  const items = [];

  // Role-specific items
  if (userRole === 'admin' || userRole === 'project_manager' || userRole === 'acquisitions' || userRole === 'technician') {
    // For managers and admins
    items.push({
      label: 'Dashboard',
      path: '/',
      icon: <Home size={18} />,
      active: location.pathname === '/'
    });
  } else {
    // For regular employees
    items.push({
      label: 'My Calendar',
      path: currentUser?.uid ? `/employee/${currentUser.uid}` : '/',
      icon: <Calendar size={18} />,
      active: location.pathname.includes(`/employee/${currentUser?.uid}`)
    });
  }

  // Inventory - available to all authenticated users
/*   items.push({
    label: 'Inventory',
    path: '/inventory',
    icon: <Package size={18} />,
    active: location.pathname.includes('/inventory')
  }); */

  // No Settings in main nav - it will be in the user dropdown

  return items;
};

  const navItems = getNavigationItems();
  return (
    <nav className="bg-[#77CCF3]  shadow-md">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 min-w-0">
          {/* Logo and Desktop Navigation */}
          <div className="flex items-center min-w-0 flex-1">
            <Link to="/" className="flex items-center gap-2 min-w-0">
              <img src={logo} alt="BL Associates" className="h-8 sm:h-10 rounded" />
              <span className="text-white font-bold text-lg sm:text-xl truncate">BL Associates</span>
            </Link>
          
            {/* Desktop Navigation */}
            <div className="hidden md:ml-8 md:flex md:space-x-4">
              {currentUser && navItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors
                    ${item.active 
                      ? 'bg-indigo-700 dark:bg-indigo-800 text-white' 
                      : 'text-indigo-200 hover:bg-indigo-500 dark:hover:bg-indigo-700 hover:text-white'
                    }`}
                >
                  <span className="mr-1">{item.icon}</span>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
          
          {/* User section and theme toggle */}
          <div className="flex items-center gap-2">
            {currentUser ? (
              <>
                {/* Cart button - only on inventory page for PM/Admin */}
                {isOnInventoryPage && canAddToCart && (
                  <button
                    onClick={openCart}
                    className="relative p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                    aria-label="Open cart"
                  >
                    <ShoppingCart size={22} />
                    {cartItemCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {cartItemCount > 99 ? '99+' : cartItemCount}
                      </span>
                    )}
                  </button>
                )}

                {/* User menu (desktop) */}
                <div className="hidden md:relative md:flex">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center text-white"
                    aria-expanded={showUserMenu}
                    aria-haspopup="true"
                  >
                    <div className="flex items-center">
                      <div className="relative">
                        {userProfile?.photoURL ? (
                          <img
                            src={userProfile.photoURL}
                            alt="Profile"
                            className="h-8 w-8 rounded-full object-cover mr-2"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-indigo-300 dark:bg-indigo-700 flex items-center justify-center mr-2">
                            <span className="text-indigo-800 dark:text-indigo-200 text-sm font-semibold">
                              {getInitials()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-left mr-1">
                        <p className="text-sm font-medium leading-tight">{getDisplayName()}</p>
                        <p className="text-xs text-indigo-200">{userRole}</p>
                      </div>
                      <ChevronDown size={16} className={`transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {/* Dropdown menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-10 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-10 ring-1 ring-black ring-opacity-5">
                    <Link
                      to="/settings"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <User size={16} className="mr-2" />
                      Profile Settings
                    </Link>
                    {userRole === 'admin'  && (
                      <Link
                        to="/admin"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Settings size={16} className="mr-2" />
                        Admin Settings
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        handleLogout();
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <LogOut size={16} className="mr-2" />
                      Logout
                    </button>
                    </div>
                  )}
                </div>
                
                {/* Mobile menu button */}
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-indigo-200 hover:text-white hover:bg-indigo-500 dark:hover:bg-indigo-700 focus:outline-none"
                  aria-expanded={isMenuOpen}
                >
                  <span className="sr-only">{isMenuOpen ? 'Close menu' : 'Open menu'}</span>
                  {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </>
            ) : (
              <>
                {/* Theme toggle for non-logged in users */}
{/*                 <EnhancedThemeToggle className="mr-4" size="sm" /> */}
                
                {/* Login/Signup buttons */}
                <div className="flex items-center flex-shrink-0">
                  <Link
                    to="/login"
                    className="text-white hover:text-indigo-200 px-2 sm:px-3 py-1 sm:py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="ml-1 sm:ml-2 bg-white text-indigo-600 hover:bg-indigo-50 px-2 sm:px-3 py-1 sm:py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap"
                  >
                    Sign Up
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state */}
      {isMenuOpen && currentUser && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                  item.active
                    ? 'bg-indigo-700 text-white'
                    : 'text-indigo-200 hover:bg-indigo-500 hover:text-white'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </Link>
            ))}

            <div className="border-t border-indigo-700 my-2 pt-2">
              <div className="flex items-center px-3 py-2">
                {userProfile?.photoURL ? (
                  <img
                    src={userProfile.photoURL}
                    alt="Profile"
                    className="h-10 w-10 rounded-full object-cover mr-3"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-indigo-300 dark:bg-indigo-700 flex items-center justify-center mr-3">
                    <span className="text-indigo-800 dark:text-indigo-200 text-base font-semibold">
                      {getInitials()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-white font-medium">{getDisplayName()}</p>
                  <p className="text-xs text-indigo-200">{userRole}</p>
                </div>
              </div>
              
              <Link
                to="/settings"
                className="flex items-center px-3 py-2 rounded-md text-base font-medium text-indigo-200 hover:bg-indigo-500 hover:text-white"
                onClick={() => setIsMenuOpen(false)}
              >
                <User size={18} className="mr-2" />
                Profile Settings
              </Link>
              
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center px-3 py-2 rounded-md text-base font-medium text-indigo-200 hover:bg-indigo-500 hover:text-white"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default ImprovedNavbar;